import {
  AutomaticProcessingStatus,
  OperationStatusLabel,
  ReceivedInvoiceAiSuggestStatusProperty,
  TimestampStatusLabel,
} from '@/context/services/invoice/type';
import { useAiSuggestStatusChecker } from '@/features/invoices/components/Details/Components/Context/AiSuggestStatus';
import { useGetErrorMessages } from '@/features/invoices/components/Details/Components/Context/ErrorMessagesProvider';
import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useTimestampStatusChecker } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import {
  hasAiSuggestResult,
  isAiSuggestSuccess,
} from '@/features/invoices/utils/aiSuggestStatus';
import { useReceivedInvoiceTransactionFlag } from '@/hooks/feature_flag/useReceivedInvoiceTransactionFlag';
import { useTranslation } from '@/i18n';
import { SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType } from 'ap-openapi';
import { useMemo } from 'react';
import { StatusTagInfo } from '../components/ProcessingStatusTag';

const AUTOMATIC_PROCESSING_STATUS_TAGS = {
  processing: {
    status: 'success',
    labelKey: 'received_invoice.automatic_processing_status_processing_label',
    customIcon: null,
  },
  transaction_ocr_success: {
    status: 'default',
    labelKey: 'received_invoice.automatic_processing_status_success_label',
    iconType: 'approval',
  },
  transaction_ocr_failure: {
    status: 'error',
    labelKey: 'received_invoice.automatic_processing_status_failure_label',
    icon: true,
  },
} as const;

export const useProcessingStatusTags = (
  aiSuggestStatus?: ReceivedInvoiceAiSuggestStatusProperty,
  automaticProcessingStatus?: AutomaticProcessingStatus
) => {
  const { t } = useTranslation();
  const isTransactionLlmOcrEnabled = useReceivedInvoiceTransactionFlag();
  const operationChecker = useOperationStatusChecker();
  const timestampChecker = useTimestampStatusChecker();
  const aiSuggestChecker = useAiSuggestStatusChecker(aiSuggestStatus);
  const { getErrorMessage } = useGetErrorMessages();
  const eDocScannerErrorMessage = getErrorMessage(
    SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType.edoc_scanner_message
  );

  const commonTagsInfo = useMemo(() => {
    const tags: StatusTagInfo[] = [];

    if (timestampChecker?.isCreating) {
      tags.push({
        status: 'success',
        label:
          TimestampStatusLabel.creating ||
          t('invoice_timestamp_creating_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    if (eDocScannerErrorMessage) {
      tags.push({
        status: eDocScannerErrorMessage.type === 'warn' ? 'warning' : 'error',
        label: eDocScannerErrorMessage.label || '',
        customIcon: undefined,
        icon: true,
      });
    }

    return tags;
  }, [timestampChecker, t, eDocScannerErrorMessage]);

  const legacyTagsInfo = useMemo<StatusTagInfo[]>(() => {
    const tags = [...commonTagsInfo];

    if (operationChecker?.isOcrProcessing) {
      tags.push({
        status: 'success',
        label:
          OperationStatusLabel.ocr_processing ||
          t('invoice_ocr_processing_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    // AI Suggest processing — placed behind the OCR and timestamp tags. The
    // default operation-status tag (e.g. 未着手, shown once OCR finishes) is
    // suppressed while AI Suggest is still running, so the row reflects the
    // in-progress state instead of a "ready" status.
    if (aiSuggestChecker.isProcessing) {
      return [
        ...tags,
        {
          status: 'success',
          label: t('received_invoice.ai_suggest_processing_label'),
          customIcon: null, // Will be added in FileNameCell
        },
      ];
    }

    if (!hasAiSuggestResult(aiSuggestStatus)) {
      return tags;
    }

    const success = isAiSuggestSuccess(aiSuggestStatus);
    return [
      ...tags,
      {
        status: success ? 'success' : 'warning',
        label: t('received_invoice.ai_suggest_label'),
        iconType: success ? 'ai' : 'caution',
        tooltip: t(
          success
            ? 'received_invoice.ai_suggest_tooltip_success'
            : 'received_invoice.ai_suggest_tooltip_failed'
        ),
      },
    ];
  }, [
    commonTagsInfo,
    operationChecker,
    aiSuggestChecker.isProcessing,
    aiSuggestStatus,
    t,
  ]);

  const automaticProcessingTagsInfo = useMemo<StatusTagInfo[]>(() => {
    const tags = [...commonTagsInfo];
    const tagConfig = automaticProcessingStatus
      ? AUTOMATIC_PROCESSING_STATUS_TAGS[automaticProcessingStatus]
      : undefined;

    if (tagConfig) {
      const { labelKey, ...tagInfo } = tagConfig;
      tags.push({ ...tagInfo, label: t(labelKey) });
    }

    return tags;
  }, [commonTagsInfo, automaticProcessingStatus, t]);

  return {
    statusTagsInfo: isTransactionLlmOcrEnabled
      ? automaticProcessingTagsInfo
      : legacyTagsInfo,
  };
};
